import React from 'react';
import { ArrowRight, Check, Building2, Hammer, Compass, Wrench } from 'lucide-react';
import API from '../apiConfig';

export function ProduktPage({ onRegister, navigate }) {
  return (
    <article className="pt-28 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-3">Produkt</div>
        <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          LV-Software für Bau, Architektur und Planung.
        </h1>
        <p className="text-lg text-zinc-400 leading-relaxed mb-12">
          LAIzyBlueprint ist eine Software-Engine, die Grundrisse aus PDF-Plänen in Sekunden in ein vollständiges,
          VOB-konformes Leistungsverzeichnis umwandelt. Statt Wandlängen mit dem Lineal abzugreifen, laden Sie
          den Plan hoch — und erhalten ein strukturiertes Mengen-LV als PDF.
        </p>

        <h2 className="text-2xl sm:text-3xl font-bold mb-6 mt-12" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Funktionsweise der Grundrissanalyse
        </h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Im Kern arbeitet LAIzyBlueprint mit einer mehrstufigen Pipeline: Zuerst werden die Vektoren und
          Maßketten direkt aus der PDF extrahiert. Anschließend identifiziert die Engine Räume, Wände,
          Türen und Fenster, klassifiziert Außen- und Innenwände nach geometrischer Hülle und kalibriert
          die Wandstärken auf DIN-Standards.
        </p>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Im optionalen Schritt analysiert die Engine zusätzlich einen Gebäudeschnitt automatisch und
          überträgt Geschosshöhen, Brüstungs- und Sturzhöhen auf den Grundriss.
        </p>

        <h2 className="text-2xl sm:text-3xl font-bold mb-6 mt-14" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Wie aus Geometrie ein Leistungsverzeichnis wird
        </h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Sobald die Geometrie erfasst ist, gruppiert LAIzyBlueprint Bauteile nach Lage, Wandstärke und Typ.
          Mauerwerks-Positionen werden mit Wandfläche (m²) und Volumen (m³) ausgewiesen, Türen und Fenster
          mit Maßen und DIN-Anschlag, Räume mit Bodenflächen.
        </p>

        <h2 className="text-2xl sm:text-3xl font-bold mb-6 mt-14" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Interaktiver Editor — alles bleibt anpassbar
        </h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Zwischen automatischer Erkennung und LV-Export liegt ein vollständiges Bearbeitungstool. Sie können
          Wände manuell einzeichnen, Polygone per Drag &amp; Drop verschieben, Wandstärken umklassifizieren,
          Räume umbenennen und Sonderpositionen wie Streifenfundament oder Attika über einen integrierten
          Schnellrechner ergänzen.
        </p>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Multi-Selektion mit Ctrl+Klick erlaubt Massen-Edits, ein einklappbarer Werkzeugkasten hält das
          Interface aufgeräumt. Erst wenn Sie zufrieden sind, exportieren Sie das LV — die Engine schlägt vor,
          Sie entscheiden.
        </p>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Sonderpositionen wie Streifenfundament oder Attika werden über manuelle LV-Positionen mit einem
          integrierten L/B/H-Schnellrechner abgebildet. Das Endergebnis ist ein <strong>Langtext-Leistungsverzeichnis
          nach VOB/C DIN 18330</strong>, exportierbar als PDF — vergabefertig.
        </p>

        <h2 className="text-2xl sm:text-3xl font-bold mb-6 mt-14" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Vorteile gegenüber manuellen Prozessen
        </h2>
        <div className="space-y-3 mb-8">
          {[
            'Mengenermittlung in unter 60 Sekunden statt Tagen',
            'Konsistente Mengen über alle Projekte hinweg',
            'Mengen-Abweichung typisch unter 1 % zur ING-Wandliste',
            'Automatische Klassifizierung Außen- vs. Innenwand',
            'Decomposition-Fix: Sturz- und Brüstungsbereich werden korrekt zur Wandfläche addiert',
            'Volle Audit-Spur: jeder Wert ist auf den Plan zurückführbar',
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-3">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
              <span className="text-zinc-200">{b}</span>
            </div>
          ))}
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold mb-6 mt-14" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Anwendungsfälle
        </h2>
        <div className="grid md:grid-cols-2 gap-4 mb-12">
          {[
            { icon: Building2, title: 'Bauunternehmen', desc: 'Schneller von der Anfrage zum Angebot — kalkulationssichere Mengen ohne Rechenfehler.' },
            { icon: Compass, title: 'Architekten', desc: 'Automatische Massenermittlung als Service-Erweiterung für Bauherren — ohne zusätzliche Bearbeiter.' },
            { icon: Hammer, title: 'Planungsbüros', desc: 'Konsistente LVs für Vergabe-Pakete in HOAI-Leistungsphasen 6 & 7.' },
            { icon: Wrench, title: 'Projektentwickler', desc: 'Frühe Kostenindikationen aus Konzeptplänen — datengetriebene Investitions-Entscheidungen.' },
          ].map((u, i) => (
            <div key={i} className="p-5 bg-zinc-900/50 border border-zinc-800/60 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <u.icon className="w-4 h-4 text-blue-400" />
                </div>
                <h3 className="font-semibold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{u.title}</h3>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">{u.desc}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-4 mt-12">
          <button onClick={onRegister} data-testid="produkt-cta" className="px-8 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg flex items-center gap-2 text-sm">
            Jetzt testen <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('kontakt')} className="px-8 py-3.5 border border-zinc-700 hover:border-zinc-500 text-zinc-200 font-medium rounded-lg text-sm">
            Demo anfragen
          </button>
        </div>
      </div>
    </article>
  );
}

export function UeberUnsPage({ onRegister, navigate }) {
  return (
    <article className="pt-28 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-3">Über uns</div>
        <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          LAIzyBlueprint — Software für eine Branche, die längst Software verdient hat.
        </h1>
        <p className="text-lg text-zinc-400 leading-relaxed mb-10">
          LAIzyBlueprint ist das Produkt der LAIzyones — einem deutschen Einzelunternehmen mit einem klaren Fokus:
          die Bauplanung digitalisieren — pragmatisch, praxisnah und ohne Buzzwords.
        </p>

        <h2 className="text-2xl sm:text-3xl font-bold mb-5 mt-12" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Innovation im Bauwesen
        </h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Während andere Branchen längst auf KI und Automatisierung setzen, hängt das Bauwesen technologisch
          oft eine Generation zurück. LAIzyBlueprint schließt diese Lücke an genau einer Stelle, an der sie täglich
          weh tut: bei der Mengenermittlung aus Plänen.
        </p>

        <h2 className="text-2xl sm:text-3xl font-bold mb-5 mt-12" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Aus realem Bedarf entwickelt
        </h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Die Engine ist nicht im Labor entstanden, sondern direkt aus der Praxis — entwickelt in enger
          Zusammenarbeit mit Bauunternehmen, Baustoffhändlern und Handwerkern, die schlicht keine Lust mehr
          hatten, jede Wandfläche von Hand zu zählen. Aus dem konkreten Wunsch „diese stundenlange
          Mengenrechnerei muss aufhören" wurde ein Produkt, das heute pro Plan in unter einer Minute liefert,
          was vorher Tage dauerte.
        </p>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Jede Funktion stammt aus einer realen Frage von Bauunternehmern, Baustoffhändlern oder Handwerkern —
          und nichts kommt ins Produkt, was nicht in der Praxis besteht.
        </p>

        <h2 className="text-2xl sm:text-3xl font-bold mb-5 mt-12" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Unsere Vision
        </h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Wir wollen, dass der gesamte Bauplanungs-Workflow in der nächsten Dekade so digital wird, wie er es
          längst sein müsste: Pläne werden eingelesen, Mengen automatisch ermittelt, Ausschreibungen aus
          strukturierten Daten generiert. Kein „Ausdrucken-Abmessen-Abtippen" mehr.
        </p>
        <p className="text-zinc-300 leading-relaxed mb-4">
          Schritt für Schritt, jedes Release ein Stück näher an einer Branche, in der niemand mehr eine
          Wandlänge mit dem Lineal abgreifen muss.
        </p>

        <div className="mt-12 p-6 bg-zinc-900/50 border border-zinc-800/60 rounded-xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Im Profil</div>
          <p className="text-zinc-200 font-semibold mb-1">LAIzyBlueprint</p>
          <p className="text-zinc-400 text-sm">Produkt der LAIzyones · Einzelunternehmen, Inhaber: David Ehrle</p>
          <p className="text-zinc-400 text-sm">Operative Projektleitung: Philipp-Arsim Nuraj</p>
          <p className="text-zinc-400 text-sm mt-2">Ferdinand-Lentjes-Straße 32 · 39167 Wellen, Deutschland</p>
          <p className="text-zinc-500 text-xs mt-3">
            Rechtliche Hinweise (AGB, Impressum, Datenschutz):{' '}
            <a href="https://www.laizyones.de" target="_blank" rel="noopener noreferrer" className="underline hover:text-zinc-300">
              www.laizyones.de
            </a>
          </p>
        </div>

        <div className="flex gap-4 mt-12">
          <button onClick={onRegister} className="px-8 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg flex items-center gap-2 text-sm">
            Jetzt testen <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('kontakt')} className="px-8 py-3.5 border border-zinc-700 hover:border-zinc-500 text-zinc-200 font-medium rounded-lg text-sm">
            Kontakt
          </button>
        </div>
      </div>
    </article>
  );
}

export function KontaktPage() {
  const [sent, setSent] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    const fd = new FormData(e.currentTarget);
    try {
      const resp = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fd.get('name'),
          email: fd.get('email'),
          company: fd.get('company') || '',
          phone: fd.get('phone') || '',
          message: fd.get('message'),
        }),
      });
      if (!resp.ok) {
        const d = await resp.json();
        throw new Error(d.detail || 'Fehler');
      }
      setSent(true);
    } catch (err) {
      setError(err.message || 'Nachricht konnte nicht gesendet werden.');
    }
    setSending(false);
  };

  return (
    <article className="pt-28 pb-24 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-3">Kontakt</div>
        <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Reden wir über Ihr nächstes Projekt.
        </h1>
        <p className="text-lg text-zinc-400 leading-relaxed mb-10">
          Egal ob Demo-Termin, Pilotprojekt oder einfach eine Frage: Wir antworten in der Regel innerhalb
          eines Werktags.
        </p>

        {sent ? (
          <div className="p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-200" data-testid="contact-success">
            Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns in Kürze bei Ihnen.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name *" name="name" required />
              <Field label="Unternehmen" name="company" />
              <Field label="E-Mail *" name="email" type="email" required />
              <Field label="Telefon" name="phone" type="tel" />
            </div>
            <label className="block">
              <span className="text-sm text-zinc-400 mb-1.5 block">Nachricht *</span>
              <textarea
                name="message"
                rows={5}
                required
                data-testid="contact-message"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-zinc-100 focus:border-blue-500/50 focus:outline-none transition-colors"
                placeholder="Erzählen Sie uns kurz von Ihrem Projekt oder Ihrer Frage."
              />
            </label>
            <div className="flex items-center justify-between gap-4 pt-2">
              <p className="text-xs text-zinc-600">
                Mit dem Absenden akzeptieren Sie unsere{' '}
                <a href="https://www.laizyones.de" target="_blank" rel="noopener noreferrer" className="underline hover:text-zinc-400">Datenschutzerklärung</a>.
              </p>
              <button type="submit" disabled={sending} data-testid="contact-submit" className="px-7 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white font-medium rounded-lg flex items-center gap-2 text-sm whitespace-nowrap">
                {sending ? 'Senden...' : <>Nachricht senden <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        )}

        <div className="mt-14 pt-10 grid sm:grid-cols-3 gap-6 text-sm">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">E-Mail</div>
            <a href="mailto:info@laizyones.de" className="text-zinc-200 hover:text-white">info@laizyones.de</a>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Telefon</div>
            <a href="tel:+491797615911" className="text-zinc-200 hover:text-white">0179 7615911</a>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Adresse</div>
            <span className="text-zinc-400">Ferdinand-Lentjes-Straße 32<br />39167 Wellen</span>
          </div>
        </div>
      </div>
    </article>
  );
}

function Field({ label, name, type = 'text', required = false }) {
  return (
    <label className="block">
      <span className="text-sm text-zinc-400 mb-1.5 block">{label}</span>
      <input
        type={type}
        name={name}
        required={required}
        data-testid={`contact-${name}`}
        className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-zinc-100 focus:border-blue-500/50 focus:outline-none transition-colors"
      />
    </label>
  );
}
